import axios, { AxiosResponse } from "axios";
import { environment } from "../../../environments/environment";
import { getValue } from "../../../utils/getValue";
import { IReferrerList } from "../../../components/Invoice/ReferrerList/types";
import { IReferredUser } from "../../../components/Invoice/ReferUserList/types";

export interface ProfileUser {
	userId: number;
	userName: string;
	roleName: string;
}

export interface Profile {
	profileId: string;
	individualName: string;
	businessName: string;
	createdDate: string;
	profileStatusName: string;
	stepCompleted: number;
	businessEmail: string;
	isFreelancer: boolean;
	receiveCurrency: string;
	receiveCountryCode: string;
	profileUsers: ProfileUser[];
}

export interface GetInvoicePartnerListResponse {
	success: boolean;
	message: string;
	data?: Profile[];
}

export interface ApproveFileResponse {
	success: boolean;
	message: string;
	data?: boolean;
}

export interface Invoice {
	invoiceNumber: string;
	invoiceCreatedDate: string;
	invoiceModifiedDate: string;
	statusName: string;
	currencyCode: string;
	to: string;
	sendAmount: number;
	receiveAmount: number;
	from: string;
	invoiceAmount: number;
	dueDate: number;
	customerId: string;
	invoiceId: number;
	userId: number;
	paymentId: string;
	isFreelancer: boolean;
	receiveCurrency: string;
	receiveCountryCode: string;
}

export interface GetInvoiceListResponse {
	success: boolean;
	message: string;
	data?: InvoiceListDataResponse;
}
export interface GetReferralListResponse {
	success: boolean;
	message: string;
	data?: ReferrerListDataResponse;
}
export interface ReferrerListDataResponse {
	pageNumber: number;
	pageSize: number;
	totalRecords: number;
	data?: IReferrerList[];
}
export interface InvoiceListDataResponse {
	pageNumber: number;
	pageSize: number;
	totalRecords: number;
	data?: Invoice[];
}
export interface PaginationBaseParams {
	pageNumber: number;
	pageSize: number;
	searchTerm?: string;
	sortField?: string;
	sortType?: string;
}

export interface InvoiceListParams extends PaginationBaseParams {
	statusFilter?: string;
}
export interface IStatusHistory {
	historyId: number;
	oldStatus: string;
	newStatus: string;
	isApprovedSnapshot: boolean;
	comment: string;
	changedByAdminUserName: string;
	changedDate: string;
}
export interface GetStatusHistoryListResponse {
	success: boolean;
	message: string;
	data?: IStatusHistory[];
}
export interface InvoiceUserListParams {
	pageNumber: number;
	pageSize: number;
	searchTerm?: string;
	sortField?: string;
	sortType?: string;
	roleNameFilter?: string;
}

// Single user item
export interface InvoiceUser {
	id: number;
	registerEmail: string;
	businessName: string | null;
	businessEmail: string | null;
	roleName: string;
	creatorProfileCreated: boolean;
	createdDate: string;
}

export interface InvoiceUserListData {
	pageNumber: number;
	pageSize: number;
	totalRecords: number;
	data: InvoiceUser[];
}

export interface InvoiceUserListResponse {
	success: boolean;
	message: string;
	data: InvoiceUserListData;
}
export interface GetReferredUsersResponse {
	success: boolean;
	message: string;
	data?: IReferredUser[];
}

interface SendInvoiceReminderResponse {
	success: boolean;
	message: string;
	data: boolean;
}
export class InvoiceService {
	private static URL: string = environment.invoiceApiUrl;

	public static async getInvoicePartnerList(): Promise<GetInvoicePartnerListResponse> {
		try {
			// const endpoint = "https://localhost:21000/v1/InvoiceAdmin/profile-list";
			const endpoint = `${this.URL}/v1/InvoiceAdmin/profile-list`;

			const config = {
				headers: {
					Authorization: `Bearer ${getValue(sessionStorage.getItem("Token"))}`
				}
			};
			const response: AxiosResponse<GetInvoicePartnerListResponse> = await axios.get(endpoint, config);
			return response.data;
		} catch (error: any) {
			throw error;
		}
	}

	public static async getInvoiceList(param?: Partial<InvoiceListParams>): Promise<GetInvoiceListResponse> {
		try {
			// const endpoint = "https://localhost:21000/v1/InvoiceAdmin/invoice-list";
			const endpoint = `${this.URL}/v1/InvoiceAdmin/invoice-list`;
			const config = {
				headers: { Authorization: `Bearer ${getValue(sessionStorage.getItem("Token"))}` }
			};
			const response: AxiosResponse<GetInvoiceListResponse> = await axios.post(endpoint, param, config);
			return response.data;
		} catch (error: any) {
			throw error;
		}
	}

	public static async updateProfileStatus(profileId: string, profileStatusId: number, infoNeededMailContent: string): Promise<ApproveFileResponse> {
		try {
			// const endpoint = "https://localhost:21000/v1/InvoiceAdmin/update-profile-status";
			const endpoint = `${this.URL}/v1/InvoiceAdmin/update-profile-status`;
			const config = {
				headers: {
					Authorization: `Bearer ${getValue(sessionStorage.getItem("Token"))}`
				}
			};
			const response: AxiosResponse<ApproveFileResponse> = await axios.put(
				endpoint,
				{ profileId, profileStatusId, infoNeededMailContent },
				config
			);
			return response.data;
		} catch (error: any) {
			throw error;
		}
	}

	public static async getStatusHistory(userId: string): Promise<GetStatusHistoryListResponse> {
		try {
			// const endpoint = "https://localhost:21000/v1/InvoiceAdmin/status-history?ProfileCreatorUserId="+userId;
			const endpoint = `${this.URL}/v1/InvoiceAdmin/status-history?ProfileCreatorUserId=${userId}`;
			const config = {
				headers: { Authorization: `Bearer ${getValue(sessionStorage.getItem("Token"))}` }
			};
			const response: AxiosResponse<GetStatusHistoryListResponse> = await axios.get(endpoint, config);
			return response.data;
		} catch (error: any) {
			throw error;
		}
	}

	public static async getInvoiceUserList(params: InvoiceUserListParams): Promise<InvoiceUserListResponse> {
		try {
			const endpoint = `${this.URL}/v1/InvoiceAdmin/invoice-user-list`;

			const config = {
				headers: {
					Authorization: `Bearer ${getValue(sessionStorage.getItem("Token"))}`
				}
			};

			const response = await axios.post(endpoint, params, config);
			return response.data;
		} catch (error) {
			throw error;
		}
	}

	public static async getReferralList(param?: Partial<PaginationBaseParams>): Promise<GetReferralListResponse> {
		try {
			// const endpoint = "https://localhost:21000/v1/InvoiceAdmin/referrer-list";
			const endpoint = `${this.URL}/v1/InvoiceAdmin/referrer-list`;
			const config = {
				headers: { Authorization: `Bearer ${getValue(sessionStorage.getItem("Token"))}` }
			};
			const response: AxiosResponse<GetReferralListResponse> = await axios.post(endpoint, param, config);
			return response.data;
		} catch (error: any) {
			throw error;
		}
	}

	public static async addReferrer(payload: { name: string; email?: string }): Promise<any> {
		try {
			// const endpoint = "https://localhost:21000/v1/InvoiceAdmin/add-referrer";
			const endpoint = `${this.URL}/v1/InvoiceAdmin/add-referrer`;
			const config = {
				headers: {
					Authorization: `Bearer ${getValue(sessionStorage.getItem("Token"))}`
				}
			};
			const response: AxiosResponse<any> = await axios.post(endpoint, payload, config);
			return response.data;
		} catch (error: any) {
			throw error;
		}
	}

	public static async getReferredUsers(referralCode?: string): Promise<GetReferredUsersResponse> {
		try {
			// const endpoint = `https://localhost:21000/v1/InvoiceAdmin/referred-users${referralCode ? `?referralCode=${referralCode}` : ''}`;
			const endpoint = `${this.URL}/v1/InvoiceAdmin/referred-users${referralCode ? `?referralCode=${referralCode}` : ""}`;
			const body = referralCode ? { referralCode } : {};
			const config = {
				headers: { Authorization: `Bearer ${getValue(sessionStorage.getItem("Token"))}` }
			};
			const response: AxiosResponse<GetReferredUsersResponse> = await axios.post(endpoint, body, config);
			return response.data;
		} catch (error: any) {
			throw error;
		}
	}

	public static async sendInvoiceReminderEmail(invoiceId: number): Promise<SendInvoiceReminderResponse> {
		try {
			const endpoint = `${this.URL}/v1/InvoiceAdmin/send-invoice-reminder-email?invoiceId=${invoiceId}`;

			const config = {
				headers: { Authorization: `Bearer ${getValue(sessionStorage.getItem("Token"))}` }
			};

			const response: AxiosResponse<SendInvoiceReminderResponse> = await axios.post(endpoint, {}, config);

			return response.data;
		} catch (error: any) {
			throw error;
		}
	}

	public static async rejectInvoice(invoiceId: number, status: string, reason: string): Promise<GetInvoiceListResponse> {
		try {
			const endpoint = `${this.URL}/v1/InvoiceAdmin/updateInvoiceStatus?invoiceId=${invoiceId}&status=${status}&reason=${reason}`;
			const config = {
				headers: { Authorization: `Bearer ${getValue(sessionStorage.getItem("Token"))}` }
			};

			const response: AxiosResponse<GetInvoiceListResponse> = await axios.post(endpoint, {}, config);

			return response.data;
		} catch (error: any) {
			throw error;
		}
	}
}
